<template>
  <div class="block block-mode-loading-oneui">
    <div class="block-header border-bottom">
      <h3 class="block-title">
        Latest Expert Responses
      </h3>
    </div>
    <div class="block-content block-content-full">
      <div
        v-if="loading"
        class="text-center py-4"
      >
        <div
          class="spinner-grow text-primary"
          role="status"
        >
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      
      <div
        v-else-if="error"
        class="alert alert-danger"
      >
        {{ error }}
      </div>
      
      <div v-else>
        <div class="table-responsive">
          <table class="table table-striped table-hover table-borderless table-vcenter font-size-sm mb-0">
            <thead class="table-dark">
              <tr class="text-uppercase">
                <th
                  class="font-w700"
                  style="width: 80px;"
                >
                  Date
                </th>
                <th
                  class="d-none d-sm-table-cell font-w700 text-center"
                  style="width: 100px;"
                >
                  Expert
                </th>
                <th
                  class="font-w700"
                  style="width: 200px;"
                >
                  Project
                </th>
                <th
                  class="font-w700"
                  style="width: 150px;"
                >
                  Client Firm
                </th>
                <th
                  class="font-w700 text-center"
                  style="width: 80px;"
                >
                  Response
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="assignment in assignments"
                :key="assignment.id"
              >
                <td>
                  <span class="font-w600">{{ formatDate(assignment.updated_at) }}</span>
                </td>
                <td class="d-none d-sm-table-cell text-center">
                  <a
                    target="_blank"
                    :href="getExpertUrl(assignment.expert.id)"
                  >
                    {{ assignment.expert.user.full_name }}
                  </a>
                </td>
                <td class="font-w600">
                  <a
                    target="_blank"
                    :href="getProjectUrl(assignment.project.id)"
                  >
                    {{ assignment.project.project_title }}
                  </a>
                </td>
                <td class="font-w600">
                  {{ assignment.project.company.name }}
                </td>
                <td class="text-center">
                  <div :class="getStatusButtonClass(assignment.status.name)">
                    {{ getStatusButtonText(assignment.status.name) }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- Pagination -->
        <nav
          v-if="pagination.last_page > 1"
          aria-label="Latest Expert Responses Pagination"
          class="mt-3"
        >
          <ul class="pagination justify-content-center">
            <li
              class="page-item"
              :class="{ disabled: parseInt(pagination.current_page) === 1 }"
            >
              <a 
                class="page-link" 
                href="#" 
                :tabindex="parseInt(pagination.current_page) === 1 ? -1 : 0"
                @click.prevent="changePage(pagination.current_page - 1)"
              >
                <i class="fa fa-angle-left" />
              </a>
            </li>
            
            <li 
              v-for="page in visiblePages" 
              :key="page" 
              class="page-item" 
              :class="{ active: parseInt(page) === parseInt(pagination.current_page) }"
            >
              <a 
                class="page-link" 
                href="#" 
                @click.prevent="changePage(page)"
              >
                {{ page }}
              </a>
            </li>
            
            <li
              class="page-item"
              :class="{ disabled: parseInt(pagination.current_page) === parseInt(pagination.last_page) }"
            >
              <a 
                class="page-link" 
                href="#" 
                :tabindex="parseInt(pagination.current_page) === parseInt(pagination.last_page) ? -1 : 0"
                @click.prevent="changePage(pagination.current_page + 1)"
              >
                <i class="fa fa-angle-right" />
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment-timezone'

export default {
  name: 'LatestExpertResponses',
  
  data() {
    return {
      maxVisiblePages: 5
    }
  },
  
  computed: {
    ...mapGetters('dashboard', [
      'latestExpertResponses',
      'latestExpertResponsesPagination',
      'latestExpertResponsesLoading',
      'latestExpertResponsesError'
    ]),
    
    assignments() {
      return this.latestExpertResponses
    },
    
    pagination() {
      return this.latestExpertResponsesPagination
    },
    
    loading() {
      return this.latestExpertResponsesLoading
    },
    
    error() {
      return this.latestExpertResponsesError
    },
    
    visiblePages() {
      const current = this.pagination.current_page
      const last = this.pagination.last_page
      const maxVisible = this.maxVisiblePages
      
      if (last <= maxVisible) {
        return Array.from({ length: last }, (_, i) => i + 1)
      }
      
      const half = Math.floor(maxVisible / 2)
      let start = Math.max(1, current - half)
      let end = Math.min(last, start + maxVisible - 1)
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1)
      }
      
      return Array.from({ length: end - start + 1 }, (_, i) => start + i)
    }
  },
  
  mounted() {
    this.fetchData()
  },
  
  methods: {
    ...mapActions('dashboard', ['fetchLatestExpertResponses']),
    
    async fetchData() {
      try {
        await this.fetchLatestExpertResponses()
      } catch (error) {
        console.error('Failed to fetch latest expert responses:', error)
      }
    },
    
    async changePage(page) {
      const targetPage = parseInt(page)
      const currentPage = parseInt(this.pagination.current_page)
      const lastPage = parseInt(this.pagination.last_page)
      
      if (targetPage < 1 || targetPage > lastPage || targetPage === currentPage) {
        return
      }
      
      try {
        await this.fetchLatestExpertResponses({ page: targetPage })
      } catch (error) {
        console.error('Failed to change page:', error)
      }
    },
    
    formatDate(dateString) {
      return moment(dateString).format('DD MMM YYYY')
    },
    
    getExpertUrl(expertId) {
      return `/admin/experts/view/${expertId}`
    },
    
    getProjectUrl(projectId) {
      return `/admin/project/details/${projectId}`
    },
    
    getStatusButtonText(statusName) {
      return statusName === 'Declined' ? 'Declined' : 'Accepted'
    },
    
    getStatusButtonClass(statusName) {
      const baseClass = 'btn btn-sm'
      return statusName === 'Declined' 
        ? `${baseClass} btn-warning` 
        : `${baseClass} btn-success`
    }
  }
}
</script>

<style scoped>
.link-fx {
  color: #007bff;
  text-decoration: none;
}

.link-fx:hover {
  color: #0056b3;
  text-decoration: underline;
}

/* Let OneUI theme handle pagination styling, just ensure proper state handling */
.page-item.disabled .page-link {
  pointer-events: none;
  cursor: default;
}
</style>
